/**
 * Single-flight Promise dedupe with optional post-failure cooldown.
 *
 * Replaces the static refresh queue in `BaseRestService`. The semantics are
 * intentionally identical so feature behaviour is preserved when consumers
 * migrate to `aos-auth`:
 *
 * - N concurrent callers share one in-flight Promise.
 * - On success, the in-flight slot is cleared and the cooldown resets.
 * - On failure, the next caller within `cooldownMs` short-circuits to the
 *   cached error (prevents bursts of 401s from re-using a refresh token
 *   that the IdP has already consumed server-side — Keycloak rejects this
 *   with "Maximum allowed refresh token reuse exceeded").
 *
 * The helper is platform-agnostic and side-effect-free except for the
 * Promise it returns. `now` is injectable so tests don't need fake timers.
 */
export interface SingleFlightOptions {
    /** Cooldown window after a failure, in ms. Default: 2_000. */
    cooldownMs?: number
    /** Clock source. Default: `Date.now`. Override in tests. */
    now?: () => number
}

export interface SingleFlight<T> {
    /**
     * Run the task. Concurrent callers get the same in-flight Promise.
     * Callers arriving inside the failure cooldown receive the cached error.
     */
    run: (task: () => Promise<T>) => Promise<T>
    /** Is a task currently in flight? */
    isInFlight: () => boolean
    /** Reset cooldown + cached error. Used after logout. */
    reset: () => void
}

export const createSingleFlight = <T>(opts: SingleFlightOptions = {}): SingleFlight<T> => {
    const cooldownMs = opts.cooldownMs ?? 2_000
    const now = opts.now ?? Date.now

    let inFlight: Promise<T> | null = null
    let cooldownUntil = 0
    let lastError: Error | null = null

    const run = (task: () => Promise<T>): Promise<T> => {
        if (inFlight) {
            return inFlight
        }
        if (now() < cooldownUntil) {
            return Promise.reject(lastError ?? new Error('Single-flight on cooldown'))
        }
        const promise = task()
            .then(result => {
                cooldownUntil = 0
                lastError = null
                return result
            })
            .catch((err: unknown) => {
                const error = err instanceof Error ? err : new Error(String(err))
                cooldownUntil = now() + cooldownMs
                lastError = error
                throw error
            })
            .finally(() => {
                inFlight = null
            })
        inFlight = promise
        return promise
    }

    const isInFlight = (): boolean => inFlight !== null

    const reset = (): void => {
        inFlight = null
        cooldownUntil = 0
        lastError = null
    }

    return { run, isInFlight, reset }
}
